import { getCoreUtils } from "./kCoreHandler";
import { kDocTheme } from "./kDocThemeCore";
import { kDocChartSvgText } from "./kDocChartSvgText";
import { kDocChartSvgAxes } from "./kDocChartSvgAxes";
import { kDocChartSvgDraw } from "./kDocChartSvgDraw";
import { kDocChartFrame, kDocChartLabel, kDocChartSvgMath } from "./kDocChartSvgMath";

// kDocChartSvg - a vector chart bound to DATA (E-05 / F-12, WP-X3b 2026-09-28). The product resolves a Chart part's
// Table figures at Generate time into a kDocChart and renders it here; the SVG goes into the existing diagram path
// (kDocDiagram Format "svg", RenderedSvg), so the PDF (svg-to-pdfkit), Word (SVG + PNG fallback) and the HTML page
// need no new block type, and a chart follows "Update figures" / "Roll forward" because it is redrawn from numbers.
//
// Kinds: "bar" (grouped columns), "stacked", "line", "barline" (the LineSeries share the ONE value axis - a second
// scale is refused by design), "pie", "donut" (the first series over the categories). The look comes from a theme
// chart style (kDocTheme.ChartStyles, X1's field; the shape is kDocChartStyle below) and the theme's colours / fonts.
// Numbers are formatted by kDocNumberFormatCore (locale, the theme's negative sign and thin spaces).
// A second consumer passes its own kDocChart; nothing here knows DocumentBuilder.

/** One series: its name and a value per category (null = no value). AsLine: drawn as a line in a "barline". */
export class kDocChartSeries {
    public Name: string = "";
    public Values: (number | null)[] = [];
    public AsLine: boolean = false;
}

/** The chart data (resolved from a Table part by the product). */
export class kDocChart {
    public Kind: string = "bar";
    public Categories: string[] = [];
    public Series: kDocChartSeries[] = [];
    // The theme chart style (kDocTheme.ChartStyles[].Name); "" = the first style, or the defaults.
    public StyleName: string = "";
    // "none" | "last" (the last value of each series) | "all".
    public ValueLabels: string = "none";
    // Decimals of the value labels and the axis; -1 = as the data needs (0 for whole numbers, else 1).
    public Decimals: number = -1;
    // Printed after every value label and axis number ("%", " Mkr"); "" = none.
    public Unit: string = "";
}

/** A theme chart style: the agreed shape of kDocTheme.ChartStyles (X1 adds the field). 0 / "" = the default. */
export class kDocChartStyle {
    public Name: string = "";
    // Hex colours per series (swatch names are resolved to hex before output).
    public SeriesColors: string[] = [];
    // "" = Theme.DiagramFontFamily, else Theme.BodyFont.
    public Font: string = "";
    public SizePt: number = 0;
    public AxisRulePt: number = 0;
    public GridRulePt: number = 0;
    public ValueLabelSizePt: number = 0;
    public TitleRuleAbovePt: number = 0;
    public BarGapPct: number = 0;
    public DonutHolePct: number = 0;
}

/** The plot rectangle and the value scale of a cartesian chart - declared in the leaf kDocChartSvgMath (2026-10-22). */
export { kDocChartFrame };

/**
 * A WEB PAGE'S NEEDS (added 2026-09-29 for WACommander's AI panel, where a chart is cleaned by kHtmlSafeCore's
 * AiAnswerPolicy, painted by the page's own CSS in light and dark, and tapped for a value). EVERY FIELD IS OFF BY
 * DEFAULT, and a chart rendered without this object (or with a fresh one) is the document output byte for byte -
 * pinned by tests/kDocChartSvg.test.ts.
 */
export class kDocChartWeb {
    /** The <svg>'s FIRST child <title> and its role="img" + aria-label (cut to AriaMax characters). "" = none. */
    public Summary: string = "";
    public AriaMax: number = 200;
    /** A <title> inside every bar and dot: "Category - Series: value" (the series name only with 2+ series). */
    public MarkTitles: boolean = false;
    /** Appended to the value in a mark title only (" messages"); axis and value labels keep kDocChart.Unit. */
    public TitleUnit: string = "";
    /**
     * Colour by CLASS, never a hex: series ClassPrefix + 1..ClassCount (cycling), gridlines and the axis rule
     * MutedClass, and text with no fill (it inherits the page's ink) - so a page's light/dark CSS paints it.
     */
    public ClassColors: boolean = false;
    public ClassPrefix: string = "c";
    public ClassCount: number = 6;
    public MutedClass: string = "muted";
    /** Text carries no font-family (it inherits the page's font). */
    public InheritFont: boolean = false;
    /** Every coordinate a whole number. */
    public WholeNumbers: boolean = false;
    /** Category labels only where they do not touch the one before (every n-th), kept inside the width. */
    public ThinCategories: boolean = false;
    /** Text content escaped as an HTML serialiser writes a TEXT node (& < >; a quote stays a quote). */
    public TextEscape: boolean = false;
    /** A line series' dot radius (0 = the document's 1.6) - a web page's dot is also a tap target. */
    public DotRadius: number = 0;
    /** Whole-number data gets whole-number axis steps (no "0.5" tick printed as "1" twice for counts of 0-3). */
    public IntegerTicks: boolean = false;
    /** A centred text never runs past the left or right edge (a value label on the last bar). */
    public KeepInside: boolean = false;
    /**
     * A value label stays inside its own bar's column (added 2026-10-14): a label wider than the column is drawn
     * smaller, down to LabelMinPct of its size, and left out when it would still reach a neighbour's bar. The column
     * is the whole category slot for one bar series or a stack, and the bar itself for grouped bars (they touch).
     */
    public LabelsInColumn: boolean = false;
    public LabelMinPct: number = 70;
    /**
     * Grouped bars (2+ bar series, not stacked; added 2026-10-14): a category's value labels are drawn centred over the
     * whole group, just above its tallest bar, largest value nearest the bars, each further one a line higher. A label
     * that would rise more than one label height above the plot is left out (the larger ones are kept). Each label is
     * fitted to the group's slot as LabelsInColumn does.
     */
    public LabelsAboveGroup: boolean = false;
    /**
     * With LabelsAboveGroup (added 2026-10-14): the value axis grows (to a further nice tick) until every label over
     * the groups fits inside the plot, so none is left out.
     */
    public LabelHeadroom: boolean = false;
    /**
     * ValueLabels "extremes" per SERIES (added 2026-10-14): each series' highest, lowest and last drawn value is
     * labelled, rather than whole categories chosen by their height (which can pick an empty category as the lowest
     * and leaves a series' own peak unlabelled when another series is taller elsewhere).
     */
    public ExtremesBySeries: boolean = false;
}

/** One value label placed over a group of bars (kDocChartWeb.LabelsAboveGroup) - declared in the leaf kDocChartSvgMath. */
export { kDocChartLabel };

export class kDocChartSvg {
    /** Labels stacked over a group sit this many label sizes apart (a browser's text box is about 1.2-1.3 tall). */
    public static LabelLine: number = kDocChartSvgMath.LabelLine;

    // Colours when the style has none (the same array as kDocChartSvgMath.DefaultColors).
    public static DefaultColors: string[] = kDocChartSvgMath.DefaultColors;

    private readonly _Chart: kDocChart;
    private readonly _Theme: kDocTheme;
    private readonly _Locale: string;
    private readonly _W: number;
    private readonly _H: number;
    private readonly _Style: kDocChartStyle;
    /** A web page's options (kDocChartWeb); a fresh object = every one off = the document output. */
    private readonly _Web: kDocChartWeb;
    private _Parts: string[] = [];

    public constructor(Chart: kDocChart, Theme: kDocTheme, Locale: string, WidthPt: number, HeightPt: number,
                       Web: kDocChartWeb | null = null) {
        this._Chart = Chart;
        this._Theme = Theme;
        this._Locale = Locale;
        this._W = Math.max(1, Number(WidthPt) || 1);
        this._H = Math.max(1, Number(HeightPt) || 1);
        this._Style = kDocChartSvg.StyleOf(Theme, Chart.StyleName);
        if (Web === null || Web === undefined) { this._Web = new kDocChartWeb(); } else { this._Web = Web; }
    }

    /**
     * The chart as an SVG document of WidthPt x HeightPt ("" when the chart has nothing to draw). `Web` (optional,
     * kDocChartWeb): a web page's needs - absent, the output is exactly the document chart.
     */
    public static Render = (Chart: kDocChart, Theme: kDocTheme, Locale: string, WidthPt: number, HeightPt: number,
                            Web: kDocChartWeb | null = null): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            return new kDocChartSvg(Chart, Theme, Locale, WidthPt, HeightPt, Web).Svg();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.Render");
            return "";
        }
    };

    /** The theme's chart style by name (the first when Name is "" or unknown); a default style when there is none. */
    public static StyleOf = (Theme: kDocTheme, Name: string): kDocChartStyle => {
        if (getCoreUtils().KErrorMode) { return new kDocChartStyle(); }
        try {
            const Styles: unknown = (Theme as unknown as { ChartStyles?: unknown }).ChartStyles;
            if (Array.isArray(Styles) === false || (Styles as kDocChartStyle[]).length === 0) { return new kDocChartStyle(); }
            const List: kDocChartStyle[] = Styles as kDocChartStyle[];
            for (const Style of List) { if (Style.Name === Name) { return Object.assign(new kDocChartStyle(), Style); } }
            return Object.assign(new kDocChartStyle(), List[0]);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.StyleOf");
            return new kDocChartStyle();
        }
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgText.Svg
    public Svg = (): string => { return kDocChartSvgText.Svg(this); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgText.SummaryAttributes
    public SummaryAttributes = (): string => { return kDocChartSvgText.SummaryAttributes(this); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgText.SummaryTitle
    public SummaryTitle = (): string => { return kDocChartSvgText.SummaryTitle(this); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgText.N
    public N = (Value: number): string => { return kDocChartSvgText.N(this, Value); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgText.EscapeText
    public EscapeText = (Text: string): string => { return kDocChartSvgText.EscapeText(this, Text); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgText.Paint
    public Paint = (Index: number): string => { return kDocChartSvgText.Paint(this, Index); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgText.MarkEnd
    public MarkEnd = (Tag: string, S: number, I: number, Value: number): string => { return kDocChartSvgText.MarkEnd(this, Tag, S, I, Value); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgText.Size
    public Size = (): number => { return kDocChartSvgText.Size(this); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgText.LabelSize
    public LabelSize = (): number => { return kDocChartSvgText.LabelSize(this); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgText.Or
    public Or = (Value: number, Fallback: number): number => { return kDocChartSvgText.Or(this, Value, Fallback); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgText.Font
    public Font = (): string => { return kDocChartSvgText.Font(this); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgText.FamilyList
    public static FamilyList = (Family: string): string => { return kDocChartSvgText.FamilyList(this, Family); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgText.ColorOf
    public ColorOf = (Index: number): string => { return kDocChartSvgText.ColorOf(this, Index); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgText.ThemeColors
    public static ThemeColors = (Theme: kDocTheme): string[] => { return kDocChartSvgText.ThemeColors(this, Theme); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgText.TextColor
    public TextColor = (): string => { return kDocChartSvgText.TextColor(this); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgText.Label
    public Label = (Value: number): string => { return kDocChartSvgText.Label(this, Value); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgText.TickLabel
    public TickLabel = (Tick: number, Ticks: number[]): string => { return kDocChartSvgText.TickLabel(this, Tick, Ticks); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgText.DataDecimals
    public DataDecimals = (): number => { return kDocChartSvgText.DataDecimals(this); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgText.Text
    public Text = (X: number, Y: number, Text: string, Anchor: string, Size: number, Color: string): string => {
        return kDocChartSvgText.Text(this, X, Y, Text, Anchor, Size, Color);
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgText.ColumnText
    public ColumnText = (X: number, Y: number, Label: string, Column: number): string => {
        return kDocChartSvgText.ColumnText(this, X, Y, Label, Column);
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgAxes.Num
    public static Num = (Value: number): string => { return kDocChartSvgAxes.Num(this, Value); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgAxes.Escape
    public static Escape = (Text: string): string => { return kDocChartSvgAxes.Escape(this, Text); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgAxes.Extent
    public Extent = (): number[] => { return kDocChartSvgAxes.Extent(this); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgAxes.Ticks
    public static Ticks = (Min: number, Max: number, Count: number, MinStep: number = 0): number[] => {
        return kDocChartSvgAxes.Ticks(this, Min, Max, Count, MinStep);
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgAxes.FrameOf
    public FrameOf = (): kDocChartFrame => { return kDocChartSvgAxes.FrameOf(this); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgAxes.YOf
    public static YOf = (Frame: kDocChartFrame, Value: number): number => { return kDocChartSvgAxes.YOf(this, Frame, Value); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgAxes.LegendHeight
    public LegendHeight = (): number => { return kDocChartSvgAxes.LegendHeight(this); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgAxes.DrawCartesian
    public DrawCartesian = (): void => { return kDocChartSvgAxes.DrawCartesian(this); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgAxes.DrawGrid
    public DrawGrid = (Frame: kDocChartFrame): void => { return kDocChartSvgAxes.DrawGrid(this, Frame); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgAxes.Rule
    public Rule = (X1: number, Y1: number, X2: number, Y2: number, Width: number, Color: string): string => {
        return kDocChartSvgAxes.Rule(this, X1, Y1, X2, Y2, Width, Color);
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgAxes.SlotOf
    public SlotOf = (Frame: kDocChartFrame, Index: number): number[] => { return kDocChartSvgAxes.SlotOf(this, Frame, Index); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgAxes.DrawCategories
    public DrawCategories = (Frame: kDocChartFrame): void => { return kDocChartSvgAxes.DrawCategories(this, Frame); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgAxes.CategoryStep
    public CategoryStep = (Frame: kDocChartFrame): number => { return kDocChartSvgAxes.CategoryStep(this, Frame); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgAxes.DrawBars
    public DrawBars = (Frame: kDocChartFrame): void => { return kDocChartSvgAxes.DrawBars(this, Frame); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgAxes.ColumnOf
    public ColumnOf = (Slot: number, Bars: number): number => { return kDocChartSvgAxes.ColumnOf(this, Slot, Bars); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgAxes.GroupLabels
    public GroupLabels = (): boolean => { return kDocChartSvgAxes.GroupLabels(this); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgAxes.GroupValues
    public GroupValues = (Index: number): number[] => { return kDocChartSvgAxes.GroupValues(this, Index); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgAxes.Descending
    public static Descending = (A: number, B: number): number => { return kDocChartSvgAxes.Descending(this, A, B); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgAxes.DrawGroupLabels
    public DrawGroupLabels = (Frame: kDocChartFrame, Index: number, Slot: number[]): void => {
        return kDocChartSvgAxes.DrawGroupLabels(this, Frame, Index, Slot);
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgAxes.DrawPlacedLabels
    public DrawPlacedLabels = (Frame: kDocChartFrame): void => { return kDocChartSvgAxes.DrawPlacedLabels(this, Frame); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgAxes.PlaceLabels
    public PlaceLabels = (Frame: kDocChartFrame): kDocChartLabel[] => { return kDocChartSvgAxes.PlaceLabels(this, Frame); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgAxes.BarBoxes
    public BarBoxes = (Frame: kDocChartFrame): number[][] => { return kDocChartSvgAxes.BarBoxes(this, Frame); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgAxes.TopIn
    public static TopIn = (Boxes: number[][], Left: number, Right: number, Floor: number): number => {
        return kDocChartSvgAxes.TopIn(this, Boxes, Left, Right, Floor);
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgAxes.Clear
    public static Clear = (Placed: kDocChartLabel[], One: kDocChartLabel, Line: number): number => {
        return kDocChartSvgAxes.Clear(this, Placed, One, Line);
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgAxes.FitLabels
    public FitLabels = (Frame: kDocChartFrame, MinStep: number): void => { return kDocChartSvgAxes.FitLabels(this, Frame, MinStep); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgAxes.Refit
    public Refit = (Frame: kDocChartFrame, MinStep: number): void => { return kDocChartSvgAxes.Refit(this, Frame, MinStep); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgAxes.LabelledValue
    public LabelledValue = (S: number, I: number): boolean => { return kDocChartSvgAxes.LabelledValue(this, S, I); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgAxes.SeriesExtremes
    public SeriesExtremes = (S: number): number[] => { return kDocChartSvgAxes.SeriesExtremes(this, S); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgAxes.Found
    public static Found = (Index: number): boolean => { return kDocChartSvgAxes.Found(this, Index); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgDraw.BarSeries
    public BarSeries = (): number[] => { return kDocChartSvgDraw.BarSeries(this); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgDraw.DrawBar
    public DrawBar = (Frame: kDocChartFrame, S: number, I: number, X: number, Width: number, Base: number, Column: number = 0): number => {
        return kDocChartSvgDraw.DrawBar(this, Frame, S, I, X, Width, Base, Column);
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgDraw.Labelled
    public Labelled = (Index: number): boolean => { return kDocChartSvgDraw.Labelled(this, Index); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgDraw.Extremes
    public Extremes = (): number[] => { return kDocChartSvgDraw.Extremes(this); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgDraw.HeightAt
    public HeightAt = (Index: number): number => { return kDocChartSvgDraw.HeightAt(this, Index); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgDraw.HeightOf
    public static HeightOf = (Chart: kDocChart, Index: number, LegacyZeroFloor: boolean = false,
                              FaultName: string = "kDocChartSvg.HeightOf"): number => {
        return kDocChartSvgDraw.HeightOf(this, Chart, Index, LegacyZeroFloor, FaultName);
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgDraw.DrawStacks
    public DrawStacks = (Frame: kDocChartFrame): void => { return kDocChartSvgDraw.DrawStacks(this, Frame); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgDraw.DrawLines
    public DrawLines = (Frame: kDocChartFrame): void => { return kDocChartSvgDraw.DrawLines(this, Frame); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgDraw.DrawLine
    public DrawLine = (Frame: kDocChartFrame, S: number): void => { return kDocChartSvgDraw.DrawLine(this, Frame, S); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgDraw.Polyline
    public Polyline = (Points: string[], Color: string, S: number = 0): void => { return kDocChartSvgDraw.Polyline(this, Points, Color, S); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgDraw.DrawLegend
    public DrawLegend = (): void => { return kDocChartSvgDraw.DrawLegend(this); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgDraw.DrawPie
    public DrawPie = (): void => { return kDocChartSvgDraw.DrawPie(this); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgDraw.Slice
    public static Slice = (Cx: number, Cy: number, R: number, Hole: number, A0: number, A1: number, Color: string): string => {
        return kDocChartSvgDraw.Slice(this, Cx, Cy, R, Hole, A0, A1, Color);
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgDraw.Point
    public static Point = (Cx: number, Cy: number, Radius: number, A: number): string => { return kDocChartSvgDraw.Point(this, Cx, Cy, Radius, A); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgDraw.DrawPieLegend
    public DrawPieLegend = (X: number): void => { return kDocChartSvgDraw.DrawPieLegend(this, X); };
}
